import { DetailedCustomer } from '../../../shared/models/customer.model';
import {
  Department,
  DepartmentWithResidents,
} from '../../../shared/models/department.model';
import {
  Resident,
  ResidentPayload,
  UpdateResident,
} from '../../../shared/models/resident.model';
import { JwtPayload, SaveDepartment, UserForm } from '../../types/interfaces';
import { Cart, ResidentCart } from '../../../shared/models/cart.model';
import { Injectable } from '@angular/core';
import { Package } from '../../../shared/models/package.model';
import { Profile } from '../../../shared/models/profile.model';
import { TOKEN_KEY } from '../../types/constants';
import { jwtDecode } from 'jwt-decode';
import { User } from '../../../shared/models/user.model';
import { DepartmentOrder } from '../../../shared/models/order.model';
import moment from 'moment';
import {
  LastOrderedProducts,
  Product,
} from '../../../shared/models/product.model';
import { TestCustomerOrderPayload } from '../../../shared/models/test-customer.model';

@Injectable({ providedIn: 'root' })
export class TestCustomerService {
  private readonly CUSTOMER_KEY = 'test_customer';
  private readonly DEPARTMENTS_KEY = 'test_customer_departments';
  private readonly CARTS_KEY = 'test_customer_carts';
  private readonly CUSTOMER_PROFILE_KEY = 'test_customer_profile';
  private readonly ORDERS_KEY = 'test_customer_orders_v2';
  private readonly LEGACY_ORDERS_KEY = 'test_customer_orders';
  private readonly FAVOURITE_PRODUCTS = 'test_customer_favourite_product';

  private tempResidentId = -1;
  private tempDepartmentId = -1;
  private tempOrderId = -1;
  private tempCartId = -1;
  private tempUserId = -1;

  isLoggedInAsTestCustomer(): boolean {
    const authToken = localStorage.getItem(TOKEN_KEY);
    if (authToken) {
      try {
        return jwtDecode<JwtPayload>(authToken).testCustomer;
      } catch (error) {
        console.error('Error decoding JWT:', error);
        return false;
      }
    } else {
      return false;
    }
  }

  saveCustomer(customer: DetailedCustomer): void {
    localStorage.setItem(this.CUSTOMER_KEY, JSON.stringify(customer));
  }

  loadCustomer(): DetailedCustomer | null {
    const raw = localStorage.getItem(this.CUSTOMER_KEY);
    if (!raw) return null;
    const customer = new DetailedCustomer(JSON.parse(raw));
    customer.departments = customer.departments.map(d => new Department(d));
    return customer;
  }

  hasCustomer(): boolean {
    return localStorage.getItem(this.CUSTOMER_KEY) !== null;
  }

  clear(): void {
    localStorage.removeItem(this.CUSTOMER_KEY);
    localStorage.removeItem(this.DEPARTMENTS_KEY);
    localStorage.removeItem(this.CARTS_KEY);
    localStorage.removeItem(this.CUSTOMER_PROFILE_KEY);
    localStorage.removeItem(this.ORDERS_KEY);
    localStorage.removeItem(this.LEGACY_ORDERS_KEY);
    localStorage.removeItem(this.FAVOURITE_PRODUCTS);
  }

  private loadAllDepartments(): DepartmentWithResidents[] {
    const raw = localStorage.getItem(this.DEPARTMENTS_KEY);
    return raw ? JSON.parse(raw) : [];
  }

  private saveAllDepartments(departments: DepartmentWithResidents[]): void {
    localStorage.setItem(this.DEPARTMENTS_KEY, JSON.stringify(departments));
  }

  generateDepartmentId(): number {
    return this.tempDepartmentId--;
  }

  saveDepartment(
    department: SaveDepartment,
    preserveResidentFavourites = false
  ): number {
    const departmentId = this.generateDepartmentId();
    const existingDepartment =
      department.departmentId && this.hasDepartment(department.departmentId)
        ? this.getDepartment(department.departmentId)
        : null;
    // Saving a department no longer carries residents (they're managed
    // separately), so fall back to the department's existing residents when
    // none are provided, rather than wiping them out.
    const residents = department.people ?? existingDepartment?.people ?? [];
    const newDepartment = new DepartmentWithResidents({
      ...department,
      people: residents.map(p => {
        const existingResident = existingDepartment?.people.find(
          resident => resident.id === p.id
        );

        return new Resident({
          ...p,
          favouriteProducts: preserveResidentFavourites
            ? existingResident?.favouriteProducts ?? p.favouriteProducts ?? []
            : p.favouriteProducts ?? [],
          id: p.id || this.generateResidentId(),
        });
      }),
      departmentId: department.departmentId || departmentId,
      id: department.departmentId || departmentId,
      isChecked: false,
      peopleCount: residents.length,
      editableInShop: 1,
    });
    const customer = this.loadCustomer();
    if (customer) {
      customer.departments = customer.departments
        .filter(d => d.id !== newDepartment.id)
        .concat(newDepartment);
      customer.departments = customer.departments.sort((a, b) =>
        a.department.localeCompare(b.department)
      );
      this.saveCustomer(customer);
    }
    const departments = this.loadAllDepartments();
    const filtered = departments.filter(d => d.id !== department.departmentId);
    filtered.push(newDepartment);
    this.saveAllDepartments(filtered);
    this.updateCarts();
    return departmentId;
  }

  hasDepartment(departmentId: number): boolean {
    const departments = this.loadAllDepartments();
    return departments.some(d => d.id === +departmentId);
  }

  getDepartment(departmentId: number): DepartmentWithResidents | null {
    const departments = this.loadAllDepartments();
    const department = departments.find(d => d.id === +departmentId) ?? null;
    department.people = department?.people.sort((a, b) =>
      a.lname.localeCompare(b.lname)
    );
    department.people = department.people.map(r => new Resident(r));
    return new DepartmentWithResidents(department);
  }

  getDepartments(): DepartmentWithResidents[] {
    return this.loadAllDepartments();
  }

  removeDepartment(departmentId: number): void {
    const customer = this.loadCustomer();
    if (customer) {
      this.saveCustomer(
        new DetailedCustomer({
          ...customer,
          departments: customer.departments.filter(
            department => department.departmentId !== departmentId
          ),
        })
      );
      const departments = this.loadAllDepartments().filter(
        d => d.id !== departmentId
      );

      this.saveAllDepartments(departments);
    }
  }

  getResident(residentId: number): Resident | null {
    const departments = this.loadAllDepartments();
    for (const department of departments) {
      const resident = department.people.find(r => r.id === +residentId);
      if (resident) {
        return new Resident(resident);
      }
    }
    return null;
  }

  hasResident(residentId: number): boolean {
    const departments = this.loadAllDepartments();
    for (const department of departments) {
      const resident = department.people.find(r => r.id === +residentId);
      if (resident) {
        return true;
      }
    }
    return false;
  }

  generateResidentId(): number {
    return this.tempResidentId--;
  }

  saveResident(resident: Resident) {
    const id = this.generateResidentId();
    const customer = this.loadCustomer();
    const savedResident = this.getResident(resident.id);
    const newCustomer = new DetailedCustomer({
      ...customer,
      departments: customer.departments.map(d => {
        if (d.departmentId === resident.departmentId) {
          return new Department({
            ...d,
            peopleCount: savedResident ? d.peopleCount : d.peopleCount + 1,
          });
        }
        return new Department(d);
      }),
    });
    this.saveCustomer(newCustomer);
    const department = this.getDepartment(resident.departmentId);
    const newDepartment = new DepartmentWithResidents({
      ...department,
      peopleCount: savedResident
        ? department?.peopleCount
        : (department?.peopleCount || 0) + 1,
      people: department.people
        .filter(p => p.id !== savedResident?.id)
        .concat(
          new Resident({
            ...resident,
            residentId: savedResident?.id || id,
            id: savedResident?.id || id,
            editableInShop: 0,
            individualPaymentStatus: null,
            residentInvoiceNo: null,
          })
        ),
    });

    this.saveDepartment(newDepartment);

    return savedResident?.id || id;
  }

  removeResident(residentId: number): void {
    const customer = this.loadCustomer();
    const resident = this.getResident(residentId);
    const newCustomer = new DetailedCustomer({
      ...customer,
      departments: customer.departments.map(d => {
        if (d.departmentId === resident.departmentId) {
          return new Department({
            ...d,
            peopleCount: d.peopleCount - 1,
          });
        }
        return new Department(d);
      }),
    });

    this.saveCustomer(newCustomer);
    const department = this.getDepartment(resident.departmentId);
    const newDepartment = new DepartmentWithResidents({
      ...department,
      peopleCount: department.peopleCount - 1,
      people: department.people.filter(p => p.id !== resident.id),
    });
    this.saveDepartment(newDepartment);
  }

  private generateCartId(): number {
    return this.tempCartId--;
  }

  updateCarts(): void {
    const departments = this.loadAllDepartments();
    const currentCarts = this.getCarts();
    const carts: Cart[] = departments.map(department => {
      const existingCart = currentCarts.find(
        cart => cart.departmentId === department.id
      );
      if (existingCart) {
        const updatedResidentCarts = department.people.map(resident => {
          const existingResidentCart = existingCart.residentCarts.find(
            rc => rc.residentId === resident.id
          );
          return (
            existingResidentCart ||
            new ResidentCart({ residentId: resident.id })
          );
        });
        return new Cart({
          ...existingCart,
          residentCarts: updatedResidentCarts,
        });
      } else {
        return new Cart({
          cartId: this.generateCartId(),
          departmentId: department.id,
          departmentName: department.department,
          residentCarts: department.people.map(
            resident => new ResidentCart({ residentId: resident.id })
          ),
        });
      }
    });
    const departmentIds = departments.map(dept => dept.id);
    const otherCarts = currentCarts.filter(
      cart => !departmentIds.includes(cart.departmentId)
    );
    localStorage.setItem(
      this.CARTS_KEY,
      JSON.stringify([...carts, ...otherCarts])
    );
  }

  saveCart(cart: Cart): void {
    const carts = this.getCarts().filter(
      c => c.departmentId !== cart.departmentId
    );
    carts.push(cart);
    localStorage.setItem(this.CARTS_KEY, JSON.stringify(carts));
  }

  saveCarts(carts: Cart[]): void {
    localStorage.setItem(this.CARTS_KEY, JSON.stringify(carts));
  }

  getCarts(): Cart[] {
    const carts = localStorage.getItem(this.CARTS_KEY);
    return carts ? JSON.parse(carts).map(cart => new Cart(cart)) : [];
  }

  hasCarts(): boolean {
    return localStorage.getItem(this.CARTS_KEY) !== null;
  }

  getCart(departmentId: number) {
    const carts = this.getCarts();
    const cart = carts.find(c => c.departmentId === departmentId);
    return cart;
  }

  emptyCart(departmentId: number): void {
    const carts = this.getCarts();
    const cartIndex = carts.findIndex(c => c.departmentId === departmentId);
    if (cartIndex !== -1) {
      carts[cartIndex].emptyEntireCart();
      localStorage.setItem(this.CARTS_KEY, JSON.stringify(carts));
    }
  }

  getPackagesCosts(cartId: number): Package[] {
    const carts = this.getCarts();
    const cart = carts.find(c => c.cartId === cartId);
    if (cart) {
      return cart.residentCarts.map(rc => ({
        residentId: rc.residentId,
        packagesCost: 0.3,
      }));
    }
    return [];
  }

  saveCustomerProfile(customerProfile: Profile): void {
    const profile = this.loadCustomerProfile();
    localStorage.setItem(
      this.CUSTOMER_PROFILE_KEY,
      JSON.stringify({ ...profile, ...customerProfile })
    );
  }

  loadCustomerProfile(): Profile | null {
    const raw = localStorage.getItem(this.CUSTOMER_PROFILE_KEY);
    return raw ? new Profile(JSON.parse(raw)) : null;
  }

  hasCustomerProfile(): boolean {
    return localStorage.getItem(this.CUSTOMER_PROFILE_KEY) !== null;
  }

  getUserById(userId: string): User | null {
    const customerProfile = this.loadCustomerProfile();
    if (customerProfile) {
      const allUsers = customerProfile.users;
      const user = allUsers.find(u => u.id.toString() === userId);
      return user || null;
    }
    return null;
  }

  generateUserId(): string {
    return (this.tempUserId--).toString();
  }

  saveUser(user: UserForm): string {
    const customerProfile = this.loadCustomerProfile();
    if (customerProfile) {
      const newUserId = this.generateUserId();
      const newUser = new User({
        ...user,
        id: newUserId,
      });
      customerProfile.users.push(newUser);
      this.saveCustomerProfile(customerProfile);
      return newUserId;
    }
    return null;
  }

  deleteUser(userId: string): void {
    const customerProfile = this.loadCustomerProfile();
    if (customerProfile) {
      customerProfile.users = customerProfile.users.filter(
        user => user.id.toString() !== userId
      );
      this.saveCustomerProfile(customerProfile);
    }
  }

  updateUser(user: User): void {
    const customerProfile = this.loadCustomerProfile();
    if (customerProfile) {
      customerProfile.users = customerProfile.users.map(u => {
        if (u.id.toString() === user.id.toString()) {
          return new User({
            ...u,
            ...user,
            id: u.id,
          });
        }
        return u;
      });
      this.saveCustomerProfile(customerProfile);
    }
  }

  hasTestCustomerOrders(): boolean {
    return localStorage.getItem(this.ORDERS_KEY) !== null;
  }

  getTestCustomerOrders(): TestCustomerOrderPayload[] {
    const raw = localStorage.getItem(this.ORDERS_KEY);
    return raw ? JSON.parse(raw) : [];
  }

  generateOrderId(): number {
    return this.tempOrderId--;
  }

  mapShopCartToOrderPayload(
    departmentOrder: DepartmentOrder
  ): TestCustomerOrderPayload {
    //@ts-ignore
    const products = departmentOrder.order.carts.flatMap(
      (cart: any) => cart.products
    );
    const department = this.getDepartment(departmentOrder.order.departmentId);

    return {
      orderProductsTotal: departmentOrder.order.total,

      subOrders: [
        {
          iconUrl: department?.iconUrl || '',
          color: null,
          shipping: 0,

          departmentId: departmentOrder.order.departmentId,
          name: departmentOrder.departmentName,
          packagesCost: departmentOrder.residents[0]?.packagesCost ?? 0,

          orderTotal: departmentOrder.order.total,

          details: products.map((p: any) => ({
            productId: p.id,
            count: p.quantity,
            customerName: null,
            productPrice: p.price,
            orderId: this.generateOrderId(),
            productName: p.name,
            imageUrl: p.imageUrl,
            poid: this.generateOrderId(),
            personId: Number(departmentOrder.residents[0].id.replace('_', '')),
          })),

          orderId: this.generateOrderId(),
          poid: this.generateOrderId(),
        },
      ],

      customerOrderType: 'B2B',
      invoiceNo: null,
      paymentStatus: 'PAYMENT_STATUS_OFFEN',

      orderShipping: 0,
      createdAt: moment().toISOString(),

      shippingPartner: 'OHNE',
      poid: this.generateOrderId(),
      invoiceTotal: departmentOrder.order.total,
      orderKind: 'DEBIT',
      shippingTrackingNo: '',
      paidAmount: 0,

      residents: departmentOrder.residents.map((r: any) => ({
        gender: null,
        residentInvoiceNo: null,

        createdAt: moment().toISOString(),
        emails: null,
        lname: r.name.split(' ').slice(1).join(' ') || r.name,
        kostenstelle: null,
        partnerId: 0,
        mark2Delete: 0,
        customerResidentId: null,
        partnerCustomerId: null,

        id: Number(r.id.replace('_', '')),
        createDate: moment().toISOString(),
        residentId: Number(r.id.replace('_', '')),
        lastModified: moment().toISOString(),

        orderCreatedAt: moment().toISOString(),
        paymentMethod: 'RECHNUNG',
        bankInfoId: null,
        residentNotes: null,
        partnerResidentId: null,
        fname: r.name.split(' ')[0],

        individualPaymentStatus: 'PAYMENT_STATUS_OFFEN',

        partnerDepartmentId: null,
        departmentId: departmentOrder.order.departmentId,
        departmentName: departmentOrder.departmentName,
        active: 1,
        roomNumber: null,
        residentNote: null,
        poid: this.generateOrderId(),
        partnerInvoiceAcknowledgementStatus: 'NOT_ACKNOWLEDGED',
        invoiceTotal: departmentOrder.order.total,

        customerId: departmentOrder.order.customerId,
        orderId: this.generateOrderId(),
        partnerLastSynchronization: null,
      })),

      invoice: `/order/${this.generateOrderId()}/invoice`,
      paymentMethod: 'PAYMENT_METHOD_RECHNUNG',
      status: 'ORDER_OPEN',
    };
  }

  saveTestCustomerOrder(departmentOrder: DepartmentOrder): void {
    const orders = this.getTestCustomerOrders();
    const mappedOrder = this.mapShopCartToOrderPayload(departmentOrder);
    orders.push(mappedOrder);
    localStorage.setItem(this.ORDERS_KEY, JSON.stringify(orders));
  }

  saveTestCustomerOrders(orders: any[]): void {
    const existingOrders = this.getTestCustomerOrders();
    const filteredOrders = orders.filter(newOrder => {
      return !existingOrders.some(
        existingOrder => existingOrder.poid === newOrder.poid
      );
    });
    const updatedOrders = existingOrders.concat(filteredOrders);
    localStorage.setItem(this.ORDERS_KEY, JSON.stringify(updatedOrders));
  }

  private loadFavouriteProduct(): { [key: number]: Product[] } {
    const raw = localStorage.getItem(this.FAVOURITE_PRODUCTS);
    if (!raw) return [];
    const favouriteProducts: { [key: number]: Product[] } = JSON.parse(raw);
    return favouriteProducts ?? {};
  }

  getFavouriteProducts(residentId: number): Product[] {
    const favouriteProducts = this.loadFavouriteProduct();
    return favouriteProducts[residentId]
      ? favouriteProducts[residentId].map(product =>
          Object.assign(new Product(), product)
        )
      : [];
  }

  updateResidentFavourites(
    residentId: number,
    value: number[],
    product?: Product
  ): void {
    const favouriteProducts = this.loadFavouriteProduct();
    if (product) {
      favouriteProducts[residentId] = favouriteProducts[residentId]
        ? [...favouriteProducts[residentId], product]
        : [product];
    } else {
      favouriteProducts[residentId] = favouriteProducts[residentId]
        ? favouriteProducts[residentId].filter(x => value.includes(x.id))
        : [];
    }

    const resident = this.getResident(residentId);
    resident.favouriteProducts = value;
    this.saveResident(resident);
    localStorage.setItem(
      this.FAVOURITE_PRODUCTS,
      JSON.stringify(favouriteProducts)
    );
  }

  getTestCustomerResidentOrdersRaw(residentId: number): LastOrderedProducts[] {
    const orders = (this.getTestCustomerOrders() ??
      []) as TestCustomerOrderPayload[];
    const residentOrders = orders
      .map(order => {
        const orderDate = order?.createdAt || '';
        const subOrders = order?.subOrders ?? [];
        const details = subOrders.flatMap(sub => sub?.details ?? []);
        const residentDetails = details.filter(
          detail => Number(detail?.personId) === residentId
        );
        return {
          orderDate,
          details: residentDetails,
        };
      })
      .filter(order => order.details.length > 0);

    if (!residentOrders.length) {
      return [];
    }

    return residentOrders.map(order => ({
      orderDate: order.orderDate,
      products: order.details.map(detail =>
        this.buildLastOrderedProductRaw(detail)
      ),
    }));
  }

  private buildLastOrderedProductRaw(detail: any): any {
    const productId = Number(detail?.productId ?? 0);
    const quantity = Number(detail?.count ?? detail?.quantity ?? 0);
    const price = Number(detail?.productPrice ?? 0);
    return {
      id: productId,
      name: detail?.productName ?? '',
      price,
      quantity,
      brand: { brandName: '' },
      images: [
        {
          src: detail?.imageUrl,
          productId,
          origin: '',
          id: 0,
        },
      ],
      categories: [
        {
          name: '',
          categoryId: 0,
          categorySort: 0,
          iconUrl: '',
        },
      ],
      availability: {
        status: 'YES',
        endDate: '',
        lastModifiedTimestamp: '',
      },
    };
  }

  mergeResidentOrdersRaw(
    endpointOrders: LastOrderedProducts[],
    testCustomerOrders: LastOrderedProducts[]
  ): LastOrderedProducts[] {
    if (!testCustomerOrders.length) return endpointOrders;
    if (!endpointOrders.length) return testCustomerOrders;

    const orders = [...endpointOrders, ...testCustomerOrders];
    const seen = new Set<string>();
    return orders;
  }
}
